import { Dispatch, SetStateAction, useEffect, useRef, useState } from "react";
import { FileRejection, useDropzone } from "react-dropzone";
import { useInterval } from "react-use";
import useFiles, { useCurrentKey } from "files/hooks/useFiles";
import { AlertCircle, Check, FileIcon, RotateCcw, Upload, X } from "lucide-react";
import prettyBytes from "pretty-bytes";
import { prop, uniqBy } from "ramda";
import { Alert, AlertDescription } from "~/components/ui/alert";
import { Button } from "~/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "~/components/ui/dialog";
import useToast from "~/components/ui/toast";
import { useDebounce } from "~/hooks/useDebounce";
import {
  completeMultipartUpload,
  FILE_BROWSER_API,
  firstPresignedUrl,
  MultipartPartUrl,
  MultipartUploadSession,
  UploadPart,
} from "~/lib/fileBrowserApi";
import { cn } from "~/lib/utils";
import rumClient from "~/utils/monitoring";

const MULTIPART_LIMIT = 50 * 1024 * 1024;

const splitFileIntoChunks = (file: File): Blob[] => {
  const maxChunks = 10000;
  const minChunkSize = 15 * 1024 * 1024; // 15 MB
  const chunkSize = Math.max(minChunkSize, file.size / maxChunks);
  const chunks: Blob[] = [];
  for (let start = 0; start < file.size; start += chunkSize) {
    const end = Math.min(start + chunkSize, file.size);
    chunks.push(file.slice(start, end));
  }
  return chunks;
};

interface FileUploadProgress {
  transferred: number;
  parts?: UploadPart[];
  uploadId?: string;
  error?: boolean;
}

interface UploadRowProps {
  file: File;
  handleRetry: (file: File) => Promise<void>;
  progress?: FileUploadProgress;
}

const UploadRow = ({ file, progress, handleRetry }: UploadRowProps) => {
  const hasError = progress && progress.error;
  const percentTransferred = progress?.transferred ? (progress.transferred / file.size) * 100 : 0;
  const showLoading = !hasError && percentTransferred > 0 && percentTransferred < 100;

  return (
    <div key={file.name} className="flex items-center gap-2 rounded-md bg-muted p-2">
      <FileIcon className="size-4 shrink-0 text-muted-foreground" />
      <span className="min-w-0 flex-1 truncate text-sm">{file.name}</span>
      <div className="flex min-w-[50px] justify-end">
        {showLoading && (
          <span className="text-sm text-muted-foreground">
            {Math.max(Math.floor(percentTransferred), 1)}%
          </span>
        )}
        {!hasError && !progress?.transferred && (
          <span className="text-sm text-muted-foreground">Waiting</span>
        )}
        {progress?.error && (
          <div className="flex items-center gap-1">
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="size-6"
              onClick={() => handleRetry(file)}
            >
              <RotateCcw className="size-3.5" />
            </Button>
            <AlertCircle className="size-4 text-destructive" />
          </div>
        )}
        {!hasError && percentTransferred >= 100 && (
          <Check className="size-4 text-[color-mix(in_srgb,var(--foreground)_60%,green)]" />
        )}
      </div>
    </div>
  );
};

const FileDropzone = ({
  files,
  setFiles,
}: {
  files: File[];
  setFiles: (files: File[]) => void;
}) => {
  const [dragErrorMessage, setDragErrorMessage] = useState("");

  const onDrop = (acceptedFiles: File[], fileRejections: FileRejection[]) => {
    setDragErrorMessage("");
    setFiles([...files, ...acceptedFiles]);
    if (fileRejections.length) {
      setDragErrorMessage(
        fileRejections[0].errors[0]?.message || "File rejected for unknown reasons"
      );
    }
  };

  const removeFile = (index: number) => {
    setFiles([...files.slice(0, index), ...files.slice(index + 1)]);
  };

  const { getRootProps, getInputProps, open } = useDropzone({
    onDrop,
    noClick: true,
    noKeyboard: true,
  });

  return (
    <div className="flex flex-col gap-2">
      <div
        {...getRootProps()}
        className="flex flex-col items-center justify-center gap-1.5 rounded-lg border border-dashed border-border bg-muted p-6 text-center"
      >
        <Upload className="size-10 text-muted-foreground" />
        <p className="mb-1.5 font-medium text-foreground/70">
          {dragErrorMessage || "Browse or drop files here to upload"}
        </p>
        <Button type="button" onClick={open}>
          Browse Files...
          <input {...getInputProps()} className="sr-only" />
        </Button>
      </div>
      {files.length > 0 && (
        <div className="flex max-h-[calc(100vh-485px)] min-h-[50px] flex-col gap-1 overflow-auto">
          {files.map(({ name, size }, index) => (
            <div
              key={`${name}-${index}`}
              className="flex items-center gap-2 rounded-md bg-muted p-2"
            >
              <FileIcon className="size-4 shrink-0 text-muted-foreground" />
              <span className="min-w-0 flex-1 truncate text-sm">{name}</span>
              <span className="whitespace-nowrap text-sm text-muted-foreground">
                {prettyBytes(size)}
              </span>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="size-6"
                onClick={() => removeFile(index)}
              >
                <X className="size-3.5" />
              </Button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const ModalBody = ({
  onClose,
  submitting,
  setSubmitting,
  uploadPrefix,
}: {
  onClose: () => void;
  submitting: boolean;
  setSubmitting: Dispatch<SetStateAction<boolean>>;
  uploadPrefix?: string;
}) => {
  const abortController = useRef<AbortController>(null);
  const currentKey = useCurrentKey();
  const { mutate } = useFiles(uploadPrefix);
  const { showSuccess, showError } = useToast();
  const [files, setFiles] = useState<File[]>([]);
  const [progress, setProgress] = useState<Record<string, FileUploadProgress>>({});
  const [uploadSpeed, setUploadSpeed] = useState(0);
  const [complete, setComplete] = useState(false);
  const [error, setError] = useState(false);
  const [retrying, setRetrying] = useState(false);

  const debouncedUploadSpeed = useDebounce(uploadSpeed, 100);
  const totalSize = files.reduce((sum, f) => sum + f.size, 0);

  // Compute overall bytes transferred from state
  const uploadedBytes = Object.values(progress).reduce((sum, progress) => {
    return sum + (progress.transferred || 0);
  }, 0);

  const overallPercent = totalSize > 0 ? (uploadedBytes / totalSize) * 100 : 0;
  const overallPercentText = `${Math.min(Math.floor(overallPercent), 100)}%`;
  const transferRate = `${prettyBytes(debouncedUploadSpeed, {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  })}/s`;

  const prevUploadedBytesRef = useRef<number>(uploadedBytes);
  const throughputUpdateInterval = 500;

  useInterval(
    () => {
      const delta = uploadedBytes - prevUploadedBytesRef.current;
      if (delta > 0) {
        setUploadSpeed(delta * (1000 / throughputUpdateInterval));
      }
      prevUploadedBytesRef.current = uploadedBytes;
    },
    submitting ? throughputUpdateInterval : null
  );

  useEffect(() => {
    if (retrying) {
      if (overallPercent === 100) {
        // All files are successfully uploaded, hide progress
        setRetrying(false);
      }
    }
  }, [retrying, overallPercent]);

  useEffect(() => {
    abortController.current = new AbortController();
    return () => {
      abortController.current?.abort("Upload cancelled");
    };
  }, []);

  const uploadChunk = (
    url: string,
    chunk: Blob,
    file: File,
    retry = { attemptsLeft: 3, delay: 1500 }
  ): Promise<string> => {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open("PUT", url);
      xhr.setRequestHeader("Content-Type", file.type);

      let prevLoaded = 0;

      xhr.upload.onprogress = (e) => {
        if (!e.lengthComputable) {
          return;
        }

        const delta = e.loaded - prevLoaded;

        setProgress((progress) => {
          const prev = progress[file.name] ?? {};
          const transferred = (prev.transferred ?? 0) + delta;
          const isMultipart = chunk.size !== file.size;
          return {
            ...progress,
            [file.name]: {
              ...prev,
              // Avoid setting as 100% if multipart, since we still need the
              // complete multipart request to successfully finish after
              transferred: isMultipart ? Math.min(transferred, file.size - 1) : transferred,
            },
          };
        });

        prevLoaded = e.loaded;
      };

      const handleRetry = () =>
        setTimeout(() => {
          uploadChunk(url, chunk, file, {
            attemptsLeft: retry.attemptsLeft - 1,
            delay: retry.delay * 2,
          })
            .then(resolve)
            .catch(reject);
        }, retry.delay);

      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          resolve(xhr.getResponseHeader("ETag") || "");
        } else if (retry.attemptsLeft > 0) {
          handleRetry();
        } else {
          reject(new Error(`Upload failed: ${xhr.status}`));
        }
      };

      xhr.onerror = () => {
        if (retry.attemptsLeft > 0) {
          handleRetry();
        } else {
          reject(new Error("Network error"));
        }
      };

      abortController.current?.signal.addEventListener("abort", () => {
        xhr.abort();
      });
      xhr.send(chunk);
    });
  };

  const uploadMultipartFile = async (file: File, key: string) => {
    const maxParallelReq = 6;
    const chunks = splitFileIntoChunks(file);
    const fileProgress = progress[file.name];
    const parts: UploadPart[] = fileProgress?.parts ?? [];
    let uploadId = fileProgress?.uploadId;

    let initialPartUrls: MultipartPartUrl[] = [];

    if (!uploadId) {
      const startResp = await fetch(`${FILE_BROWSER_API}/multipart-uploads`, {
        method: "POST",
        signal: abortController.current?.signal,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          key,
          file_type: file.type,
          part_count: Math.min(maxParallelReq, chunks.length),
        }),
      });

      if (!startResp.ok) {
        throw new Error("Unable to initiate multipart upload");
      }

      const session: MultipartUploadSession = await startResp.json();
      uploadId = session.upload_id;
      initialPartUrls = session.parts;

      setProgress((progress) => {
        const prev = progress[file.name] ?? {};
        return { ...progress, [file.name]: { ...prev, uploadId } };
      });
    }

    for (let startPartNum = 1; startPartNum <= chunks.length; startPartNum += maxParallelReq) {
      // Jump ahead on part number if retrying and there is already progress made from previous attempt
      if (parts.length && startPartNum === 1) {
        const existingParts = fileProgress?.parts ?? [];
        const restartPartNum = Math.max(...existingParts.map(({ part_num }) => part_num), 0);
        if (restartPartNum) {
          startPartNum = restartPartNum + 1;
        }
      }

      // Get pre-signed multi-part upload urls
      const endPartNum =
        startPartNum + Math.min(maxParallelReq, chunks.length - (startPartNum - 1));
      let partUrls = startPartNum === 1 ? initialPartUrls : [];
      if (!partUrls.length) {
        const presignedResp = await fetch(
          `${FILE_BROWSER_API}/multipart-uploads/${encodeURIComponent(uploadId)}/part-urls`,
          {
            method: "POST",
            signal: abortController.current?.signal,
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ key, start: startPartNum, end: endPartNum }),
          }
        );

        if (!presignedResp.ok) {
          throw new Error("Unable to generate pre-signed URLs");
        }

        partUrls = await presignedResp.json();
      }

      await Promise.all(
        partUrls.map(async ({ part_num, url }) => {
          const etag = await uploadChunk(url, chunks[part_num - 1], file);
          const part = { etag, part_num };
          parts.push(part);
          setProgress((progress) => {
            const prev = progress[file.name] ?? {};
            const prevParts = prev.parts ?? [];
            return { ...progress, [file.name]: { ...prev, parts: [...prevParts, part] } };
          });
        })
      );
    }

    const sortedParts = parts.sort((a, b) => a.part_num - b.part_num);
    const uniqueParts = uniqBy(prop("part_num"), sortedParts);

    await completeMultipartUpload(key, uploadId, uniqueParts, abortController.current?.signal);

    setProgress((progress) => {
      const prev = progress[file.name] ?? {};
      return {
        ...progress,
        [file.name]: { ...prev, transferred: file.size },
      };
    });
  };

  const uploadSingleFile = async (file: File, key: string) => {
    const presignedResp = await fetch(`${FILE_BROWSER_API}/upload-urls`, {
      method: "POST",
      signal: abortController.current?.signal,
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ files: [{ key, file_type: file.type }] }),
    });
    if (!presignedResp.ok) {
      throw new Error("Unable to get upload URL");
    }
    const url = firstPresignedUrl(await presignedResp.json());
    await uploadChunk(url, file, file);
    setProgress((progress) => {
      const prev = progress[file.name] ?? {};
      return {
        ...progress,
        [file.name]: { ...prev, transferred: file.size },
      };
    });
  };

  const uploadFiles = async (files: File[]) => {
    let anyFailed = false;
    for (const file of files) {
      const key = `${uploadPrefix ?? currentKey}${file.name}`;
      const fn = file.size < MULTIPART_LIMIT ? uploadSingleFile : uploadMultipartFile;
      try {
        await fn(file, key);
      } catch (err) {
        console.error(err);
        rumClient?.recordError(err);
        anyFailed = true;
        setError(true);
        setProgress((progress) => {
          const prev = progress[file.name];
          return {
            ...progress,
            [file.name]: { ...prev, error: true },
          };
        });
      }
    }

    setSubmitting(false);
    setComplete(true);
    await mutate();

    if (anyFailed) {
      showError(files.length > 1 ? "Some files failed to upload" : "Unable to upload file");
    } else {
      showSuccess(
        files.length > 1 ? `Uploaded ${files.length} files` : `Uploaded "${files[0]?.name}"`
      );
    }
  };

  const retryUpload = async (file: File) => {
    setError(false);
    setRetrying(true);
    setSubmitting(true);
    setProgress((progress) => {
      const prev = progress[file.name] ?? {};
      return { ...progress, [file.name]: { ...prev, error: false } };
    });
    await uploadFiles([file]);

    if (!Object.values(progress).some(({ error }) => error)) {
      setRetrying(false);
    }
  };

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        setSubmitting(true);
        uploadFiles(files);
      }}
      className="flex flex-col gap-4"
    >
      <DialogHeader>
        <DialogTitle className="flex items-center gap-2">
          {!submitting && !complete && "Upload Files"}
          {submitting && !retrying && "Uploading Files"}
          {complete && "Upload Complete"}
          {files.length > 0 && (
            <span className="text-sm font-normal text-muted-foreground">
              ({files.length} total, {prettyBytes(totalSize)})
            </span>
          )}
        </DialogTitle>
        <DialogDescription className="sr-only">
          Upload one or more files to the current directory
        </DialogDescription>
      </DialogHeader>

      {(submitting || retrying) && (
        <div className="flex flex-col gap-1">
          <span className="text-sm font-medium">Total Progress</span>
          <div className="h-2 w-full overflow-hidden rounded-full bg-muted">
            <div
              className="h-full rounded-full bg-primary transition-[width]"
              style={{ width: `${Math.min(overallPercent, 100)}%` }}
            />
          </div>
          <div className="flex justify-between">
            <span className="text-sm text-muted-foreground">Transfer Rate: {transferRate}</span>
            <span className="text-sm text-muted-foreground">{overallPercentText}</span>
          </div>
        </div>
      )}
      {error && (
        <Alert variant="destructive">
          <AlertDescription>Error uploading files</AlertDescription>
        </Alert>
      )}
      {!submitting && !complete && <FileDropzone files={files} setFiles={setFiles} />}
      {(submitting || complete) && (
        <div className={cn("flex flex-col gap-1")}>
          {files.map((file) => (
            <UploadRow
              key={file.name}
              file={file}
              progress={progress[file.name]}
              handleRetry={retryUpload}
            />
          ))}
        </div>
      )}
      <DialogFooter>
        {!complete && !submitting && (
          <>
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={!files.length}>
              Upload
            </Button>
          </>
        )}
        {submitting && (
          <Button type="button" variant="outline" onClick={onClose}>
            Cancel Upload
          </Button>
        )}
        {complete && (
          <Button type="button" onClick={onClose}>
            Close
          </Button>
        )}
      </DialogFooter>
    </form>
  );
};

interface UploadFileDialogProps {
  open: boolean;
  onClose: () => void;
  uploadPrefix?: string;
}

/**
 * Shadcn rebuild of `files/components/UploadFileDialog`, shared by the
 * nextgen embedded project Files tab and the personal dataset Files tab.
 * Same upload/multipart/retry logic as the original; only the
 * dialog/dropzone/progress-bar chrome changed.
 */
export const UploadFileDialog = ({ open, onClose, uploadPrefix }: UploadFileDialogProps) => {
  const [submitting, setSubmitting] = useState(false);

  const handleClose = () => {
    onClose();
    // Matches the dialog's own closing animation (`duration-200` in
    // `~/components/ui/dialog`) so the content doesn't flash back to the
    // empty upload state while it's still visibly closing.
    setTimeout(() => setSubmitting(false), 200);
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next && !submitting) {
          handleClose();
        }
      }}
    >
      <DialogContent>
        <ModalBody
          onClose={handleClose}
          submitting={submitting}
          setSubmitting={setSubmitting}
          uploadPrefix={uploadPrefix}
        />
      </DialogContent>
    </Dialog>
  );
};
